// frontend/src/pages/InquiryPage.jsx
import { useState, useEffect, useRef } from 'react';
import { useInRouterContext, useLocation, useSearchParams } from 'react-router-dom';
import { inquiryAPI } from '../api/client';
import AppShell from '../components/AppShell';
import Announcements from '../components/Announcements';
import useIsMobile from '../hooks/useIsMobile';
import { IconX, IconHelp } from '@tabler/icons-react';
import { COLORS, TONES, SHADOW, RADIUS, card, input } from '../utils/uiStyles';
import { Page, PageHeader, Button, IconButton, Pill, Tag, EmptyState, LoadingState } from '../components/ui';
import { alertDialog, confirmDialog } from '../components/AppDialog';

const DEFAULT_TYPES = ['권한/계정', '오류 신고', '개선 요청', '사용/문의', '자료/문서', '기타'];
const MAX_FILES = 5;

// 색 — var(--gw-…, <라이트 hex>)(components/ui 와 같은 보간 · 다크 전환은 CSS 변수만 바꾼다)
const V = {
  text: `var(--gw-text, ${COLORS.text})`,
  secondary: `var(--gw-secondary, ${COLORS.textSub})`,
  muted: `var(--gw-muted, ${COLORS.textMuted})`,
  line: `var(--gw-line, ${COLORS.borderSoft})`,
  border: `var(--gw-border, ${COLORS.border})`,
  surface: `var(--gw-surface, ${COLORS.surface})`,
  bg: `var(--gw-bg, ${COLORS.bg})`,
};

const fmtDate = (s) => {
  if (!s) return '';
  const d = new Date(s.endsWith('Z') ? s : s + 'Z');
  return d.toLocaleString('ko-KR', { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' });
};

const fileLabel = (f) => `${f.name || 'download'}${f.size ? ` (${Math.ceil(f.size / 1024).toLocaleString()} KB)` : ''}`;

// 문의 답변 알림(/inquiries?inquiry=<id>) — 주소 인자를 라우터에서 읽어 의존성으로 둔다.
// 🔴 이미 문의 화면에 있을 때 알림을 누르면 navigate 가 같은 Route 로 가 다시 마운트되지 않는다 —
//    마운트 때 한 번만 읽으면 무반응이다. 그래서 이동 한 번(location.key)마다 알린다(같은 알림을 또 눌러도).
// 🔴 useSearchParams 는 라우터 밖에서 던진다 — 라우터 없이 이 화면을 그리는 시험이 있어 라우터 안에서만 그린다.
function InquiryLinkParam({ onParam }) {
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const raw = searchParams.get('inquiry');
  const onParamRef = useRef(onParam);
  onParamRef.current = onParam;
  useEffect(() => {
    if (raw) onParamRef.current(raw);
  }, [raw, location.key]);
  return null;
}

export default function InquiryPage() {
  const mobile = useIsMobile();
  const inRouter = useInRouterContext();
  const fileInputRef = useRef(null);
  const loadedRef = useRef(false);   // 목록을 한 번이라도 받았나
  const [pendingInquiry, setPendingInquiry] = useState(null);   // 알림이 가리킨 문의 { id } — 목록을 받은 뒤 펼친다
  const [scrollToId, setScrollToId] = useState(null);
  const [inquiries, setInquiries] = useState([]);
  const [types, setTypes] = useState(DEFAULT_TYPES);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ title: '', content: '', inquiry_type: '', file_ids: [], files: [] });
  const [editingId, setEditingId] = useState(null);
  const [expanded, setExpanded] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const [listRes, typeRes] = await Promise.all([
        inquiryAPI.list(),
        inquiryAPI.types().catch(() => ({ data: { types: DEFAULT_TYPES } })),
      ]);
      setInquiries(listRes.data);
      setTypes(typeRes.data.types || DEFAULT_TYPES);
      loadedRef.current = true;
    } catch {
      setError('문의 목록을 불러오지 못했습니다.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  // 알림이 가리킨 문의를 받아 둔다. 이미 목록을 받아 둔 화면이면(같은 화면에서 알림을 누름)
  // 답변이 막 달렸을 수 있으니 목록을 다시 읽는다 — 펼치기는 읽기가 끝난 뒤(아래 효과).
  // 읽기가 실패한 채인 화면도 다시 읽는다(안 읽으면 아래 효과가 error 를 보고 그 문의를 바로 버린다).
  const openFromParam = (raw) => {
    setPendingInquiry({ id: raw });
    if (loadedRef.current || error) load();
  };

  // 목록(페이지 나눔 없음)을 받은 뒤 판정 — 있으면 펼치고 그 문의로 스크롤, 찾든 못 찾든 주소에서 inquiry 를 지운다.
  // 🔴 읽기가 실패해도(error) 기다리던 문의를 버리고 주소를 비운다 — 남겨 두면 나중에 다른 이유로 목록을 다시 읽을 때
  //    (문의 등록·삭제·«다시 불러오기») 그 문의가 뜻밖에 펼쳐지고 스크롤된다.
  useEffect(() => {
    if (!pendingInquiry || loading) return;
    const target = error ? null : inquiries.find((q) => String(q.id) === String(pendingInquiry.id));
    setPendingInquiry(null);
    if (target) {
      setExpanded(target.id);
      setScrollToId(target.id);
    }
    const params = new URLSearchParams(window.location.search);
    params.delete('inquiry');
    const qs = params.toString();
    window.history.replaceState(window.history.state, '',
      `${window.location.pathname}${qs ? `?${qs}` : ''}${window.location.hash || ''}`);
  }, [pendingInquiry, loading, error, inquiries]);

  // 펼친 카드가 그려진 뒤 스크롤한다(jsdom 에는 scrollIntoView 가 없다).
  useEffect(() => {
    if (scrollToId == null) return;
    const el = document.getElementById(`inquiry-${scrollToId}`);
    if (el && typeof el.scrollIntoView === 'function') el.scrollIntoView({ block: 'start' });
    setScrollToId(null);
  }, [scrollToId]);

  const resetForm = () => setForm({ title: '', content: '', inquiry_type: '', file_ids: [], files: [] });
  const openNew = () => { setEditingId(null); resetForm(); setShowForm(true); };
  const closeForm = () => { setShowForm(false); setEditingId(null); resetForm(); };   // 닫기 X·«취소» 공용
  const openEdit = (q) => {
    const qFiles = (q.files || []).filter((f) => (f.role || 'q') === 'q');
    setEditingId(q.id);
    setForm({
      title: q.title || '',
      content: q.content || '',
      inquiry_type: q.inquiry_type || '',
      file_ids: qFiles.map((f) => f.id),
      files: qFiles,
    });
    setShowForm(true);
  };

  const uploadFiles = async (files) => {
    if (!files.length) return;
    const available = MAX_FILES - form.files.length;
    if (available <= 0) {
      alertDialog(`첨부 파일은 최대 ${MAX_FILES}개까지 가능합니다.`);
      return;
    }
    const targets = files.slice(0, available);
    if (files.length > available) {
      alertDialog(`최대 ${MAX_FILES}개까지만 첨부할 수 있어 일부 파일은 제외되었습니다.`);
    }
    setUploading(true);
    try {
      for (const file of targets) {
        const isImage = file.type.startsWith('image/');
        const limit = isImage ? 5 * 1024 * 1024 : 20 * 1024 * 1024;
        const label = isImage ? '5 MB' : '20 MB';
        if (file.size > limit) {
          alertDialog(`${file.name}: 파일 크기는 ${label}를 초과할 수 없습니다.`);
          continue;
        }
        const formData = new FormData();
        formData.append('file', file);
        formData.append('role', 'q');
        const r = await inquiryAPI.uploadFile(formData);
        setForm((prev) => ({
          ...prev,
          file_ids: [...prev.file_ids, r.data.id],
          files: [...prev.files, { id: r.data.id, name: r.data.name, size: r.data.size, mime_type: r.data.mime_type, role: 'q' }],
        }));
      }
    } catch (e) {
      alertDialog(e.response?.data?.detail || '파일 업로드에 실패했습니다.');
    } finally {
      setUploading(false);
    }
  };

  const handleFileInput = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = '';
    await uploadFiles(files);
  };

  const removeFormFile = (id) => {
    setForm((prev) => ({
      ...prev,
      file_ids: prev.file_ids.filter((fid) => fid !== id),
      files: prev.files.filter((f) => f.id !== id),
    }));
  };

  const submit = async () => {
    const title = form.title.trim();
    const content = form.content.trim();
    const inquiry_type = form.inquiry_type;
    if (!inquiry_type) { alertDialog('문의 유형을 선택해 주십시오.'); return; }
    if (!title || !content) { alertDialog('제목과 내용을 입력해 주십시오.'); return; }
    setSaving(true);
    try {
      const payload = { title, content, inquiry_type, file_ids: form.file_ids };
      if (editingId) await inquiryAPI.update(editingId, payload);
      else await inquiryAPI.create(payload);
      setShowForm(false);
      setEditingId(null);
      resetForm();
      await load();
    } catch (e) {
      alertDialog(e.response?.data?.detail || '저장에 실패했습니다.');
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    if (!(await confirmDialog('이 문의를 삭제하시겠습니까?', { danger: true }))) return;
    try { await inquiryAPI.remove(id); await load(); } catch { alertDialog('삭제에 실패했습니다.'); }
  };

  const downloadFile = async (file) => {
    try {
      const res = await inquiryAPI.downloadFile(file.id);
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = file.name || 'download';
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      alertDialog(e.response?.status === 410 ? '파일 원본이 존재하지 않습니다.' : '다운로드에 실패했습니다.');
    }
  };

  const renderFiles = (files, role) => {
    const rows = (files || []).filter((f) => (f.role || 'q') === role);
    if (!rows.length) return null;
    return (
      <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', margin: '8px 0 12px' }}>
        {rows.map((f) => (
          <button key={f.id} onClick={() => downloadFile(f)} aria-label={`첨부파일 다운로드: ${f.name || 'download'}`}
            style={{ border: `1px solid ${COLORS.primaryBorder}`, background: COLORS.infoSoft, color: COLORS.info, borderRadius: RADIUS.chip, padding: '5px 10px', cursor: 'pointer', fontSize: '12px', maxWidth: '100%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {fileLabel(f)}
          </button>
        ))}
      </div>
    );
  };

  const inquiryStatusCounts = inquiries.reduce((acc, q) => {
    acc.total += 1;
    if (q.status === 'answered') acc.answered += 1;
    else acc.waiting += 1;
    return acc;
  }, { total: 0, waiting: 0, answered: 0 });

  return (
    <AppShell app="inquiries" style={{ fontFamily: 'var(--gw-font)', background: V.bg }}>
      {inRouter && <InquiryLinkParam onParam={openFromParam} />}
      <div style={{ flex: 1, overflowY: 'auto', minWidth: 0 }}>
        <Announcements scope="inquiries" />
        <Page>
          {/* 🔴 요약은 span 하나 · 안에 span 을 두지 않는다 — e2e 가 요약 밖 span 의 «답변 대기/완료» 를 세어 이 글자와 맞춘다. */}
          <PageHeader
            icon={IconHelp}
            tone="pink"
            title="문의게시판"
            subtitle={!loading && !error ? (
              <span aria-label="문의 상태 요약">전체 {inquiryStatusCounts.total} · 답변 대기 {inquiryStatusCounts.waiting} · 답변 완료 {inquiryStatusCounts.answered}</span>
            ) : null}
            actions={<Button variant="primary" onClick={openNew}>새 문의</Button>}
          />
          {loading && <LoadingState>문의 목록을 불러오는 중입니다.</LoadingState>}
          {!loading && error && (
            <div style={{ textAlign: 'center', background: TONES.red.soft, color: TONES.red.ink, borderRadius: RADIUS.lg, padding: '24px', fontSize: '13px' }}>
              <div style={{ marginBottom: '12px' }}>{error}</div>
              <Button variant="secondary" size="sm" onClick={load}>다시 불러오기</Button>
            </div>
          )}
          {!loading && !error && inquiries.length === 0 && <EmptyState description="등록된 문의가 없습니다." />}

          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {!loading && !error && inquiries.map((q) => {
              const answered = q.status === 'answered';
              const open = expanded === q.id;
              return (
                <div key={q.id} id={`inquiry-${q.id}`} style={{ ...card, padding: 0, overflow: 'hidden' }}>
                  <div onClick={() => setExpanded(open ? null : q.id)}
                    style={{ padding: mobile ? '12px' : '12px 16px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '10px', flexWrap: mobile ? 'wrap' : 'nowrap' }}>
                    {/* Pill·Tag 를 span 으로 다시 감싸지 않는다 — 감싸면 e2e 상태 계수가 두 번 센다. */}
                    <Pill tone={answered ? 'green' : 'yellow'}>{answered ? '답변 완료' : '답변 대기'}</Pill>
                    {q.inquiry_type && <Tag tone="blue">{q.inquiry_type}</Tag>}
                    <span style={{ fontSize: '14px', fontWeight: '500', color: V.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: '1 1 220px', minWidth: 0 }}>{q.title}</span>
                    <span style={{ flexShrink: 0, fontSize: '12px', color: V.muted }}>{fmtDate(q.created_at)}</span>
                    <span style={{ flexShrink: 0, fontSize: '12px', color: V.muted }}>{open ? '접기' : '열기'}</span>
                  </div>

                  {open && (
                    <div style={{ padding: '0 16px 14px', borderTop: `1px solid ${V.line}` }}>
                      <div style={{ fontSize: '14px', color: V.text, whiteSpace: 'pre-wrap', wordBreak: 'break-word', padding: '14px 0 4px', lineHeight: 1.7 }}>{q.content}</div>
                      {renderFiles(q.files, 'q')}

                      {answered ? (
                        <div style={{ background: COLORS.primarySoft, border: `1px solid ${COLORS.primaryBorder}`, borderRadius: RADIUS.lg, padding: '12px 14px' }}>
                          <div style={{ fontSize: '12px', fontWeight: '700', color: COLORS.selectedText, marginBottom: '6px' }}>
                            관리자 답변 <span style={{ color: COLORS.textSub, fontWeight: '500' }}>· {fmtDate(q.answered_at)}{q.answered_by_name ? ` · ${q.answered_by_name}` : ''}</span>
                          </div>
                          <div style={{ fontSize: '14px', color: COLORS.text, whiteSpace: 'pre-wrap', wordBreak: 'break-word', lineHeight: 1.7 }}>{q.answer}</div>
                          {renderFiles(q.files, 'a')}
                        </div>
                      ) : (
                        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                          <Button variant="secondary" size="sm" onClick={() => openEdit(q)}>수정</Button>
                          <Button variant="danger" size="sm" onClick={() => remove(q.id)}>삭제</Button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </Page>

        {showForm && (
          <div style={{ position: 'fixed', inset: 0, background: 'var(--gw-overlay, #19191945)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1100, padding: '20px' }}>
            {/* 값은 공용 AppDialog 와 같다(폭 520 · r10 · pad 25 · ≤600 은 .gw-dialog 가 pad 21·h2 18) — 입력·첨부가 있는 폼이라 부품은 따로 둔다. */}
            <div role="dialog" aria-modal="true" aria-labelledby="inquiry-form-title" className="gw-dialog"
              style={{ background: V.surface, border: `1px solid ${V.border}`, borderRadius: RADIUS.xl, boxShadow: SHADOW.float, padding: '25px', width: '520px', maxWidth: '100%', maxHeight: 'calc(100dvh - 40px)', overflowY: 'auto', boxSizing: 'border-box' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px', marginBottom: '16px' }}>
                <h2 id="inquiry-form-title" style={{ margin: 0, fontSize: '20px', fontWeight: 700, color: V.text }}>{editingId ? '문의 수정' : '새 문의'}</h2>
                <IconButton icon={IconX} label="닫기" onClick={closeForm} disabled={saving} />
              </div>
              <select value={form.inquiry_type} onChange={(e) => setForm({ ...form, inquiry_type: e.target.value })} aria-label="문의 유형"
                style={{ ...input, marginBottom: '12px', background: V.surface, color: V.text }}>
                <option value="" disabled>문의 유형</option>
                {types.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
              <input autoFocus value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="제목" aria-label="제목"
                style={{ ...input, marginBottom: '12px', background: V.surface, color: V.text }} />
              <div
                onDragOver={(e) => { e.preventDefault(); if (e.dataTransfer?.types?.includes('Files')) setDragOver(true); }}
                onDragLeave={() => setDragOver(false)}
                onDrop={(e) => { e.preventDefault(); setDragOver(false); uploadFiles(Array.from(e.dataTransfer?.files || [])); }}
                style={{ border: dragOver ? `2px dashed ${COLORS.primaryBorder}` : `1px solid ${V.border}`, borderRadius: RADIUS.md, background: dragOver ? COLORS.primarySoft : V.surface, marginBottom: '12px' }}>
                {/* outline 을 넣지 않는다 — select·input 과 같이 키보드 포커스 링(index.html :focus-visible)이 보이게. */}
                <textarea value={form.content} onChange={(e) => setForm({ ...form, content: e.target.value })} placeholder="문의 내용을 입력해 주십시오." aria-label="문의 내용" rows={7}
                  style={{ width: '100%', padding: '8px 10px', border: 'none', borderRadius: RADIUS.md, fontSize: '13px', boxSizing: 'border-box', resize: 'vertical', fontFamily: 'inherit', lineHeight: 1.6, background: 'transparent' }} />
                <div style={{ borderTop: `1px solid ${V.line}`, padding: '8px 10px', display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
                  <Button variant="secondary" size="sm" onClick={() => fileInputRef.current?.click()} disabled={uploading || form.files.length >= MAX_FILES} aria-label="파일 첨부">
                    파일 첨부
                  </Button>
                  <input ref={fileInputRef} type="file" multiple onChange={handleFileInput} style={{ display: 'none' }} />
                  <span style={{ fontSize: '12px', color: V.muted }}>{form.files.length}/{MAX_FILES} · 이미지 5 MB · 문서 20 MB</span>
                </div>
              </div>
              {form.files.length > 0 && (
                <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginBottom: '16px' }}>
                  {form.files.map((f) => (
                    <span key={f.id} style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', maxWidth: '100%', padding: '5px 8px', borderRadius: RADIUS.chip, background: COLORS.infoSoft, color: COLORS.info, fontSize: '12px' }}>
                      <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{fileLabel(f)}</span>
                      <button onClick={() => removeFormFile(f.id)} aria-label="첨부 삭제" style={{ background: 'none', border: 'none', color: V.secondary, cursor: 'pointer', padding: 0, display: 'inline-flex', alignItems: 'center' }}><IconX size={13} stroke={1.8} /></button>
                    </span>
                  ))}
                </div>
              )}
              {/* 푸터 오른쪽 — 취소 먼저, 등록 나중(시안 F 대화상자) */}
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', flexWrap: 'wrap', marginTop: '4px' }}>
                <Button variant="secondary" onClick={closeForm} disabled={saving}>취소</Button>
                <Button variant="primary" onClick={submit} disabled={saving || uploading}>{saving ? '저장 중...' : (editingId ? '수정' : '등록')}</Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </AppShell>
  );
}
